import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime, bufferTime, filter } from 'rxjs/operators';

import { markEntityAsDeleted, markEntityAsDirty, markEntitiesAsDirtyBatch } from '../../global';

import { FORCE_ACTIONS } from './actions';

@Injectable()
export class ForceEffects {
    // Batch dirty entity marks to reduce entityChangeCache$ emissions
    private dirtyEntitySubject$ = new Subject<string>();

    constructor(
        private actions$: Actions,
        private store: Store<any>
    ) {
        // Set up debounced batch processing of dirty marks
        this.dirtyEntitySubject$
            .pipe(
                bufferTime(50), // Collect all dirty marks over 50ms
                filter((ids) => ids.length > 0)
            )
            .subscribe((entityIds) => {
                // Deduplicate IDs
                const uniqueIds = [...new Set(entityIds)];
                if (uniqueIds.length === 1) {
                    this.store.dispatch(markEntityAsDirty({ entityType: 'forces', entityId: uniqueIds[0] }));
                } else {
                    this.store.dispatch(markEntitiesAsDirtyBatch({ entityType: 'forces', entityIds: uniqueIds }));
                }
            });

        // Mark forces as dirty when they're updated, but NOT when only the timestamp is updated
        // UPDATE_FORCE_TIMESTAMP is used after successful sync to update the server timestamp
        this.actions$
            .pipe(ofType(FORCE_ACTIONS.UPDATE_FORCE, FORCE_ACTIONS.ADD_FORCE, FORCE_ACTIONS.UPSERT_FORCE))
            .subscribe((changeData) => {
                this.dirtyEntitySubject$.next(changeData.force.id);
            });

        // UPDATE_FORCES_BATCH updates derived cost/appVersion in the store after processForce.
        // We intentionally do NOT mark forces dirty here. The user action that triggered the
        // cost change (e.g. ADD_UNIT_OPTION) already marks the force dirty via dirtyEntitySubject$,
        // and by the time the sync debounce fires the cost will be in the store. Marking dirty
        // here caused entityChangeCache$ to re-emit with all forces dirty, which triggered a
        // second full forces$ processing pass that reprocessed every force despite nothing
        // meaningful having changed.

        this.actions$
            .pipe(
                ofType(
                    FORCE_ACTIONS.ADD_POINTS_ADJUSTMENT,
                    FORCE_ACTIONS.REMOVE_POINTS_ADJUSTMENT,
                    FORCE_ACTIONS.DELETE_PLATOON,
                    FORCE_ACTIONS.DUPLICATE_PLATOON,
                    FORCE_ACTIONS.ADD_PLATOON,
                    FORCE_ACTIONS.ADD_UNIT,
                    FORCE_ACTIONS.ADD_UNIT_OPTION,
                    FORCE_ACTIONS.REMOVE_UNIT_OPTION
                )
            )
            .subscribe((changeData) => {
                this.dirtyEntitySubject$.next(changeData.forceId);
            });

        this.actions$.pipe(ofType(FORCE_ACTIONS.DELETE_FORCE)).subscribe((changeData) => {
            this.store.dispatch(markEntityAsDeleted({ entityType: 'forces', entityId: changeData.id, updatedAt: changeData.updatedAt }));
        });
    }
}
